import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Building2, Users, MessagesSquare, Plus, Loader2, LogOut, Copy, Check,
  ShieldCheck, TrendingUp, ExternalLink, LayoutDashboard, BarChart3,
  Search, Settings2, KeyRound, RefreshCw, Trash2, MailWarning, Activity,
} from "lucide-react";

const PLANS = ["beta", "starter", "growth", "enterprise"];
const PIE_COLORS = ["#2ac67b", "#00e5ff", "#ffd600", "#a78bfa", "#fb7185"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "clients", label: "Clients", icon: Building2 },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
];

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-white/[0.07] bg-[#091310] p-5 transition-colors hover:border-white/15" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl" style={{ background: accent, opacity: 0.12 }} />
      <div className="relative flex items-center justify-between">
        <span className="font-mono-x text-[11px] uppercase tracking-[0.15em] text-white/50">{label}</span>
        <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${accent}1a`, color: accent }}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>
      <div className="relative mt-3 font-display text-3xl font-bold text-white">{value}</div>
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div className="rounded-2xl border border-white/[0.07] bg-[#091310] p-5" data-testid={testid}>
      <h3 className="mb-4 font-mono-x text-[12px] uppercase tracking-[0.18em] text-white/60">{title}</h3>
      {children}
    </div>
  );
}

const tooltipStyle = { background: "#0a1512", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12, color: "#fff" };

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("overview");
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ business_name: "", admin_name: "", admin_email: "", admin_password: "" });
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");

  // Detail drawer
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/tenants").then(({ data }) => setTenants(data)).catch(() => {});
    api.get("/admin/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const createTenant = async () => {
    if (!form.business_name || !form.admin_name || !form.admin_email || !form.admin_password) {
      toast.error("Please fill in all fields");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/tenants", form);
      setResult({ ...data, password: form.admin_password });
      setForm({ business_name: "", admin_name: "", admin_email: "", admin_password: "" });
      setOpen(false);
      load();
      toast.success("Client workspace created");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      const { data } = await api.patch(`/admin/tenants/${t.id}`, { is_active: !t.is_active });
      setTenants((prev) => prev.map((x) => (x.id === t.id ? { ...x, is_active: data.is_active } : x)));
      if (detail && detail.id === t.id) setDetail({ ...detail, is_active: data.is_active });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const openDetail = async (id) => {
    setDetailId(id);
    setDetail(null);
    setNewPw("");
    setConfirmDelete(false);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/tenants/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDetailLoading(false);
    }
  };

  const changePlan = async (plan) => {
    setBusy("plan");
    try {
      await api.patch(`/admin/tenants/${detail.id}`, { plan });
      setDetail({ ...detail, plan });
      setTenants((prev) => prev.map((x) => (x.id === detail.id ? { ...x, plan } : x)));
      toast.success(`Plan set to ${plan}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const regenKey = async () => {
    setBusy("key");
    try {
      const { data } = await api.post(`/admin/tenants/${detail.id}/regenerate-key`);
      setDetail({ ...detail, api_key: data.api_key });
      setTenants((prev) => prev.map((x) => (x.id === detail.id ? { ...x, api_key: data.api_key } : x)));
      toast.success("API key regenerated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const resetPw = async () => {
    setBusy("pw");
    try {
      const { data } = await api.post(`/admin/tenants/${detail.id}/reset-password`);
      setNewPw(data.password);
      toast.success("Password reset — share the new one");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const delTenant = async () => {
    setBusy("delete");
    try {
      await api.delete(`/admin/tenants/${detail.id}`);
      setTenants((prev) => prev.filter((x) => x.id !== detail.id));
      setDetailId(null);
      setDetail(null);
      load();
      toast.success("Client deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); setConfirmDelete(false); }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const filtered = useMemo(() => tenants.filter((t) => {
    if (planFilter !== "all" && (t.plan || "beta") !== planFilter) return false;
    if (search && !(`${t.name} ${t.admin_email || ""}`.toLowerCase().includes(search.toLowerCase()))) return false;
    return true;
  }), [tenants, planFilter, search]);

  const kpis = (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatCard icon={Building2} label="Total Clients" value={stats?.tenants ?? "—"} accent="#2ac67b" />
      <StatCard icon={TrendingUp} label="Active" value={stats?.active_tenants ?? "—"} accent="#22c55e" />
      <StatCard icon={Users} label="Agents" value={stats?.agents ?? "—"} accent="#00e5ff" />
      <StatCard icon={MessagesSquare} label="Conversations" value={stats?.conversations ?? "—"} accent="#ffd600" />
    </div>
  );

  const convChart = (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={analytics?.series || []} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
        <defs>
          <linearGradient id="gConv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2ac67b" stopOpacity={0.5} />
            <stop offset="100%" stopColor="#2ac67b" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gMsg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00e5ff" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#00e5ff" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
        <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} />
        <Area type="monotone" dataKey="conversations" stroke="#2ac67b" strokeWidth={2} fill="url(#gConv)" />
        <Area type="monotone" dataKey="messages" stroke="#00e5ff" strokeWidth={2} fill="url(#gMsg)" />
      </AreaChart>
    </ResponsiveContainer>
  );

  const planPie = (
    <ResponsiveContainer width="100%" height={230}>
      <PieChart>
        <Pie data={analytics?.plans || []} dataKey="count" nameKey="plan" cx="50%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={3}>
          {(analytics?.plans || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />)}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} />
      </PieChart>
    </ResponsiveContainer>
  );

  const topClients = (
    <div className="space-y-2.5" data-testid="top-clients">
      {(analytics?.top_tenants || []).map((t, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="w-4 font-mono-x text-xs text-white/40">{i + 1}</span>
          <span className="flex-1 truncate text-sm text-white/85">{t.name}</span>
          <span className="font-mono-x text-sm font-semibold text-[#2ac67b]">{t.conversations}</span>
        </div>
      ))}
      {(!analytics?.top_tenants || analytics.top_tenants.length === 0) && <p className="text-sm text-white/40">No data yet.</p>}
    </div>
  );

  const clientsTable = (
    <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-[#091310]">
      <div className="flex flex-col gap-3 border-b border-white/5 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-mono-x text-[12px] uppercase tracking-[0.2em] text-white/60">Client Workspaces</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" aria-hidden="true" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients…" className="w-44 rounded-lg border border-white/10 bg-black/30 py-1.5 pl-8 pr-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-[#2ac67b]/50" data-testid="client-search" />
          </div>
          <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className="rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm capitalize text-white outline-none focus:border-[#2ac67b]/50" data-testid="plan-filter">
            <option value="all">All plans</option>
            {PLANS.map((p) => <option key={p} value={p} className="bg-[#091310] capitalize">{p}</option>)}
          </select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5 text-left font-mono-x text-[11px] uppercase tracking-[0.1em] text-white/40">
              <th className="px-5 py-3 font-medium">Business</th>
              <th className="px-5 py-3 font-medium">Admin</th>
              <th className="px-5 py-3 font-medium">Agents</th>
              <th className="px-5 py-3 font-medium">Chats</th>
              <th className="px-5 py-3 font-medium">Plan</th>
              <th className="px-5 py-3 font-medium">Active</th>
              <th className="px-5 py-3 font-medium text-right">Manage</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id} className="border-b border-white/5 last:border-0 transition-colors hover:bg-white/[0.03]" data-testid={`tenant-row-${t.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2 font-medium text-white">
                    <span className={`h-2 w-2 rounded-full ${t.is_active ? "bg-[#2ac67b] shadow-[0_0_8px_rgba(42,198,123,0.8)]" : "bg-white/20"}`} />
                    {t.name}
                  </div>
                </td>
                <td className="px-5 py-3 font-mono-x text-xs text-white/50">{t.admin_email || "—"}</td>
                <td className="px-5 py-3 font-mono-x text-white/80">{t.agent_count}</td>
                <td className="px-5 py-3 font-mono-x text-white/80">{t.conversation_count}</td>
                <td className="px-5 py-3"><span className="rounded border border-[#2ac67b]/20 bg-[#2ac67b]/10 px-2 py-0.5 font-mono-x text-[10px] uppercase tracking-wider text-[#2ac67b]">{t.plan}</span></td>
                <td className="px-5 py-3"><Switch checked={t.is_active} onCheckedChange={() => toggleActive(t)} data-testid={`toggle-active-${t.id}`} /></td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => openDetail(t.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1.5 text-xs text-white/70 transition-colors hover:border-[#2ac67b]/40 hover:text-[#2ac67b]" data-testid={`manage-tenant-${t.id}`}>
                    <Settings2 className="h-3.5 w-3.5" /> Manage
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-white/40">No clients match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#050a08] text-white">
      {/* Sidebar */}
      <aside className="hidden w-64 flex-col justify-between border-r border-white/5 bg-[#070f0c] p-5 lg:flex">
        <div>
          <img src={LOGO_URL} alt="SuperChat" className="mb-8 h-12 w-auto object-contain" />
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#2ac67b]/20 bg-[#2ac67b]/10 px-3 py-2 text-sm">
            <ShieldCheck className="h-4 w-4 text-[#2ac67b]" aria-hidden="true" />
            <span className="font-medium">Platform Console</span>
          </div>
          <nav className="space-y-1">
            {NAV.map((n) => (
              <button key={n.key} onClick={() => setView(n.key)} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${view === n.key ? "bg-[#2ac67b]/15 font-medium text-[#2ac67b]" : "text-white/55 hover:bg-white/5 hover:text-white"}`} data-testid={`nav-${n.key}`}>
                <n.icon className="h-4 w-4" aria-hidden="true" /> {n.label}
              </button>
            ))}
          </nav>
        </div>
        <div>
          <div className="mb-3 border-t border-white/10 pt-3 text-sm">
            <div className="font-medium">{user?.name}</div>
            <div className="font-mono-x text-xs text-white/40">{user?.email}</div>
          </div>
          <button onClick={doLogout} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/60 transition-colors hover:bg-white/5" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-white/5 bg-[#050a08]/80 px-6 py-4 backdrop-blur-xl">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight capitalize">{view === "overview" ? "Platform Overview" : view === "clients" ? "Client Management" : "Analytics"}</h1>
            <p className="text-sm text-white/45">
              {view === "overview" ? "Monitor platform health and growth." : view === "clients" ? "Onboard, configure and manage client businesses." : "Conversation volume, plans and top accounts."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* mobile nav */}
            <select value={view} onChange={(e) => setView(e.target.value)} className="rounded-lg border border-white/10 bg-black/30 px-2 py-2 text-sm capitalize lg:hidden" data-testid="mobile-nav-select">
              {NAV.map((n) => <option key={n.key} value={n.key} className="bg-[#091310]">{n.label}</option>)}
            </select>
            <button onClick={doLogout} className="rounded-lg p-2 text-white/50 hover:bg-white/5 lg:hidden" data-testid="admin-logout-mobile"><LogOut className="h-5 w-5" /></button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2 rounded-full bg-[#2ac67b] text-black hover:bg-[#22c55e] hover:shadow-[0_0_20px_rgba(42,198,123,0.4)]" data-testid="onboard-client-button">
                  <Plus className="h-4 w-4" /> Onboard client
                </Button>
              </DialogTrigger>
              <DialogContent className="border-white/10 bg-[#091310] text-white" data-testid="onboard-dialog">
                <DialogHeader><DialogTitle className="font-display">Onboard a new client</DialogTitle><DialogDescription className="text-white/45">Create a business workspace and its admin login.</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-white/70">Business name</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Acme Inc." data-testid="onboard-business-name" /></div>
                  <div><Label className="text-white/70">Admin full name</Label><Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} placeholder="Jane Doe" data-testid="onboard-admin-name" /></div>
                  <div><Label className="text-white/70">Admin email</Label><Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="jane@acme.com" data-testid="onboard-admin-email" /></div>
                  <div><Label className="text-white/70">Temporary password</Label><Input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} placeholder="Set a password" data-testid="onboard-admin-password" /></div>
                </div>
                <DialogFooter>
                  <Button onClick={createTenant} disabled={creating} className="w-full rounded-full bg-[#2ac67b] text-black hover:bg-[#22c55e]" data-testid="submit-onboard-button">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create workspace"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <div className="space-y-6 p-6">
          {view === "overview" && (
            <>
              {kpis}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2"><ChartCard title="Activity · last 14 days" testid="chart-activity">{convChart}</ChartCard></div>
                <ChartCard title="Plan distribution" testid="chart-plans">
                  {planPie}
                  <div className="mt-3 space-y-1.5">
                    {(analytics?.plans || []).map((p, i) => (
                      <div key={p.plan} className="flex items-center gap-2 text-sm">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="flex-1 capitalize text-white/70">{p.plan}</span>
                        <span className="font-mono-x text-white/50">{p.count}</span>
                      </div>
                    ))}
                  </div>
                </ChartCard>
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <ChartCard title="Top clients by chats" testid="card-top-clients"><div className="lg:col-span-1">{topClients}</div></ChartCard>
                <div className="lg:col-span-2">{clientsTable}</div>
              </div>
            </>
          )}

          {view === "clients" && (<>{kpis}{clientsTable}</>)}

          {view === "analytics" && (
            <>
              {kpis}
              <ChartCard title="Conversations & messages · last 14 days" testid="analytics-activity">{convChart}</ChartCard>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <ChartCard title="Messages per day" testid="analytics-messages">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={analytics?.series || []} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                      <Bar dataKey="messages" fill="#2ac67b" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Plan distribution" testid="analytics-plans">{planPie}</ChartCard>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Client detail drawer */}
      <Sheet open={!!detailId} onOpenChange={(o) => { if (!o) { setDetailId(null); setDetail(null); } }}>
        <SheetContent className="w-full overflow-y-auto border-white/10 bg-[#070f0c] text-white sm:max-w-md" data-testid="tenant-detail-drawer">
          <SheetHeader><SheetTitle className="text-white">Client details</SheetTitle></SheetHeader>
          {detailLoading && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#2ac67b]" /></div>}
          {detail && (
            <div className="mt-5 space-y-5">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#2ac67b]/15 text-lg font-bold text-[#2ac67b]">{detail.name?.[0]?.toUpperCase()}</div>
                <div className="min-w-0">
                  <div className="truncate text-lg font-semibold">{detail.name}</div>
                  <div className="truncate font-mono-x text-xs text-white/45">{detail.admin_email}</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                {[["Agents", detail.agent_count], ["Chats", detail.conversation_count], ["Messages", detail.message_count]].map(([l, v]) => (
                  <div key={l} className="rounded-xl border border-white/10 bg-black/20 py-3">
                    <div className="font-display text-xl font-bold text-white">{v}</div>
                    <div className="font-mono-x text-[10px] uppercase tracking-wider text-white/40">{l}</div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black/20 px-3 py-2.5">
                <span className="flex items-center gap-2 text-sm text-white/70"><Activity className="h-4 w-4" /> Active</span>
                <Switch checked={detail.is_active} onCheckedChange={() => toggleActive(detail)} data-testid="detail-toggle-active" />
              </div>

              <div>
                <Label className="text-white/60">Plan</Label>
                <select value={detail.plan || "beta"} onChange={(e) => changePlan(e.target.value)} disabled={busy === "plan"} className="mt-1 w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm capitalize outline-none focus:border-[#2ac67b]/50" data-testid="detail-plan-select">
                  {PLANS.map((p) => <option key={p} value={p} className="bg-[#091310] capitalize">{p}</option>)}
                </select>
              </div>

              <div>
                <Label className="text-white/60">Widget API key</Label>
                <div className="mt-1 flex items-center gap-2">
                  <div className="relative flex-1 rounded-lg border border-white/10 bg-black/30 p-2.5 pr-9">
                    <code className="block truncate font-mono-x text-xs text-[#9fe7d8]">{detail.api_key}</code>
                    <button onClick={() => copy(detail.api_key, "dkey")} className="absolute right-2 top-2.5 text-white/50 hover:text-white" data-testid="detail-copy-key">
                      {copied === "dkey" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                  <button onClick={regenKey} disabled={busy === "key"} className="flex items-center gap-1 rounded-lg border border-white/10 px-2.5 py-2.5 text-xs text-white/70 hover:border-[#2ac67b]/40 hover:text-[#2ac67b]" data-testid="detail-regenerate-key">
                    {busy === "key" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <button onClick={resetPw} disabled={busy === "pw"} className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/10 py-2.5 text-sm text-white/80 hover:bg-white/5" data-testid="detail-reset-password">
                  {busy === "pw" ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Reset admin password
                </button>
                {newPw && (
                  <div className="flex items-center gap-2 rounded-lg border border-[#2ac67b]/30 bg-[#2ac67b]/5 p-2.5" data-testid="detail-new-password">
                    <MailWarning className="h-4 w-4 flex-shrink-0 text-[#2ac67b]" />
                    <code className="flex-1 truncate font-mono-x text-xs text-[#9fe7d8]">{newPw}</code>
                    <button onClick={() => copy(newPw, "npw")} className="text-white/50 hover:text-white">{copied === "npw" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
                  </div>
                )}
              </div>

              <a href={`/widget/${detail.api_key}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-lg border border-[#2ac67b]/30 py-2.5 text-sm font-medium text-[#2ac67b] hover:bg-[#2ac67b]/5" data-testid="detail-open-widget">
                <ExternalLink className="h-4 w-4" /> Preview widget
              </a>

              {detail.recent_conversations?.length > 0 && (
                <div>
                  <Label className="text-white/60">Recent conversations</Label>
                  <div className="mt-1.5 space-y-1.5">
                    {detail.recent_conversations.map((c) => (
                      <div key={c.id} className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                        <div className="flex items-center justify-between">
                          <span className="truncate text-sm text-white/85">{c.visitor_name}</span>
                          <span className="font-mono-x text-[10px] uppercase text-white/40">{c.status}</span>
                        </div>
                        {c.last_message && <p className="truncate text-xs text-white/40">{c.last_message}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-white/10 pt-4">
                {!confirmDelete ? (
                  <button onClick={() => setConfirmDelete(true)} className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-500/30 py-2.5 text-sm font-medium text-red-400 hover:bg-red-500/10" data-testid="detail-delete-client">
                    <Trash2 className="h-4 w-4" /> Delete client
                  </button>
                ) : (
                  <div className="space-y-2 rounded-lg border border-red-500/30 bg-red-500/5 p-3">
                    <p className="text-sm text-red-300">Permanently delete <b>{detail.name}</b> and all its data?</p>
                    <div className="flex gap-2">
                      <button onClick={() => setConfirmDelete(false)} className="flex-1 rounded-lg border border-white/10 py-2 text-sm text-white/70 hover:bg-white/5">Cancel</button>
                      <button onClick={delTenant} disabled={busy === "delete"} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-red-500 py-2 text-sm font-semibold text-white hover:bg-red-600" data-testid="detail-confirm-delete">
                        {busy === "delete" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Onboarding result dialog */}
      <Dialog open={!!result} onOpenChange={() => setResult(null)}>
        <DialogContent className="border-white/10 bg-[#091310] text-white" data-testid="onboard-result-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><Check className="h-5 w-5 text-[#2ac67b]" /> Workspace ready</DialogTitle><DialogDescription className="text-white/45">Credentials for the new client. Copy and share securely.</DialogDescription></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-white/50">Share these credentials with <b className="text-white">{result?.tenant?.name}</b>. They can sign in at <b className="text-white">/login</b>.</p>
            {[
              ["Login email", result?.admin?.email, "email"],
              ["Password", result?.password, "pw"],
              ["Widget API key", result?.tenant?.api_key, "key"],
            ].map(([label, val, k]) => (
              <div key={k}>
                <Label className="font-mono-x text-[11px] uppercase tracking-[0.15em] text-white/50">{label}</Label>
                <div className="relative rounded-lg border border-white/10 bg-black/30 p-2.5 pr-10">
                  <code className="break-all font-mono-x text-xs text-[#9fe7d8]">{val}</code>
                  <button onClick={() => copy(val, k)} className="absolute right-2 top-2 text-white/50 hover:text-white">
                    {copied === k ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setResult(null)} className="w-full rounded-full bg-[#2ac67b] text-black hover:bg-[#22c55e]">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
